// Downloads a rendered report element as an A4 PDF. The element is captured
// as it appears on screen (so Hebrew and right-to-left layout are exact)
// and cut into pages between rows and cards rather than through them.

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 10;
const MAX_CANVAS_HEIGHT = 30000; // stay below browser canvas limits
const MIN_PAGE_FILL = 0.3; // never leave a page less than 30% full to keep a block whole

// Blocks that should not be split across pages
const KEEP_TOGETHER = ".keep-together, tr";

export const pageBreaks = (element, pageHeightPx) => {
  const origin = element.getBoundingClientRect().top;
  const blocks = [...element.querySelectorAll(KEEP_TOGETHER)].map((block) => {
    const rect = block.getBoundingClientRect();
    return { top: rect.top - origin, bottom: rect.bottom - origin };
  });
  const total = element.scrollHeight;
  const breaks = [0];
  let start = 0;
  while (start + pageHeightPx < total) {
    const pageEnd = start + pageHeightPx;
    const earliestBreak = start + pageHeightPx * MIN_PAGE_FILL;
    let end = pageEnd;
    const crossing = blocks.filter(
      (block) => block.top < pageEnd && block.bottom > pageEnd && block.top > earliestBreak,
    );
    if (crossing.length) end = Math.min(...crossing.map((block) => block.top));
    breaks.push(end);
    start = end;
  }
  breaks.push(total);
  return breaks;
};

/**
 * @param element   the rendered report (its buttons are left out)
 * @param fileName  name of the downloaded .pdf file
 */
export const downloadElementAsPdf = async (element, fileName) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);

  const scale = Math.min(2, MAX_CANVAS_HEIGHT / element.scrollHeight);
  const canvas = await html2canvas(element, {
    scale,
    backgroundColor: "#ffffff",
    useCORS: true,
    ignoreElements: (node) => node.classList?.contains("MuiButton-root") || node.classList?.contains("pdf-hide"),
  });

  const contentWidthMm = A4_WIDTH_MM - 2 * MARGIN_MM;
  const contentHeightMm = A4_HEIGHT_MM - 2 * MARGIN_MM;
  const pxPerMm = element.offsetWidth / contentWidthMm;
  const breaks = pageBreaks(element, contentHeightMm * pxPerMm);
  const ratio = canvas.width / element.offsetWidth;

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageCount = breaks.length - 1;
  for (let page = 0; page < pageCount; page += 1) {
    const sourceY = Math.round(breaks[page] * ratio);
    const sourceHeight = Math.round((breaks[page + 1] - breaks[page]) * ratio);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = sourceHeight;
    const context = slice.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);

    if (page > 0) pdf.addPage();
    const heightMm = sourceHeight / ratio / pxPerMm;
    pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", MARGIN_MM, MARGIN_MM, contentWidthMm, heightMm);
    pdf.setFontSize(8);
    pdf.setTextColor(148, 163, 184);
    pdf.text(`${page + 1} / ${pageCount}`, A4_WIDTH_MM / 2, A4_HEIGHT_MM - 4, { align: "center" });
  }
  pdf.save(fileName);
};
